import * as React from 'react';
import {
  AgnesButton,
  AgnesCheckbox,
  AgnesInput,
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
} from '@agnes/ui';
import { ChevronDown, X } from 'lucide-react';

import type { RubricFilterOption } from '@/services/ServiceRubricaLote';

export default function FilterMultiSelect<T extends string | number>({
  'data-qa': dataQa,
  label,
  options,
  values,
  onToggle,
}: {
  'data-qa': string;
  label: string;
  options: RubricFilterOption<T>[];
  values: T[];
  onToggle: (value: T) => void;
}) {
  const [search, setSearch] = React.useState('');
  const labelId = React.useId();
  const valueId = React.useId();
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const optionLabel = (option: RubricFilterOption<T>) =>
    option.label === String(option.value) ? option.label : `${option.value} - ${option.label}`;
  const matches = options.filter((option) =>
    normalize(optionLabel(option)).includes(normalize(search.trim())),
  );

  return (
    <div data-qa={dataQa} className="space-y-2">
      <p data-qa={`${dataQa}-label`} id={labelId} className="text-sm font-medium text-agnes-text">
        {label}
      </p>
      <AgnesPopover onOpenChange={() => setSearch('')} modal={false}>
        <AgnesPopoverTrigger asChild>
          <AgnesButton
            data-qa={`${dataQa}-trigger`}
            variant="outlined"
            className="w-full justify-between"
            aria-labelledby={`${labelId} ${valueId}`}
            disabled={!options.length}
          >
            <span data-qa={`${dataQa}-valor`} id={valueId}>
              {!options.length
                ? 'Nenhuma opção disponível'
                : values.length
                  ? `${values.length} selecionado${values.length === 1 ? '' : 's'}`
                  : 'Selecionar'}
            </span>
            <ChevronDown
              data-qa={`${dataQa}-icone-expandir`}
              className="h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          </AgnesButton>
        </AgnesPopoverTrigger>
        <AgnesPopoverContent
          data-qa={`${dataQa}-popover`}
          align="start"
          collisionPadding={12}
          aria-labelledby={labelId}
          className="flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-24px)] flex-col gap-2 p-3"
        >
          <AgnesInput
            data-qa={`${dataQa}-busca`}
            aria-label={`Pesquisar ${label.toLowerCase()}`}
            placeholder="Pesquisar por código ou nome"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div
            data-qa={`${dataQa}-opcoes`}
            className="min-h-0 max-h-56 space-y-2 overflow-y-auto"
            role="group"
            aria-label={`Opções de ${label.toLowerCase()}`}
          >
            {matches.length ? (
              matches.map((option) => (
                <AgnesCheckbox
                  key={option.value}
                  data-qa={`${dataQa}-opcao-${encodeURIComponent(option.value)}`}
                  label={
                    <span
                      data-qa={`${dataQa}-opcao-${encodeURIComponent(option.value)}-texto`}
                      className="break-words"
                    >
                      {optionLabel(option)}
                    </span>
                  }
                  checked={values.includes(option.value)}
                  onCheckedChange={() => onToggle(option.value)}
                />
              ))
            ) : (
              <p data-qa={`${dataQa}-vazio`} className="text-sm text-agnes-text-muted">
                Nenhuma opção encontrada.
              </p>
            )}
          </div>
        </AgnesPopoverContent>
      </AgnesPopover>
      {values.length > 0 && (
        <div data-qa={`${dataQa}-selecionados`} className="flex flex-wrap gap-1">
          {values.map((value) => {
            const option = options.find((entry) => entry.value === value);
            const text = option ? optionLabel(option) : String(value);
            return (
              <button
                key={value}
                data-qa={`${dataQa}-remover-${encodeURIComponent(value)}`}
                type="button"
                aria-label={`Remover ${label.toLowerCase()}: ${text}`}
                className="flex max-w-full items-center gap-1 rounded bg-agnes-primary/10 px-2 py-1 text-left text-xs text-agnes-primary"
                onClick={() => onToggle(value)}
              >
                <span
                  data-qa={`${dataQa}-remover-${encodeURIComponent(value)}-texto`}
                  className="min-w-0 break-words"
                >
                  {text}
                </span>
                <X
                  data-qa={`${dataQa}-remover-${encodeURIComponent(value)}-icone`}
                  className="h-3 w-3 shrink-0"
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
